import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { css } from '@styled-system/css';
import shouldForwardProp from '@styled-system/should-forward-prop';
import { useResetAtom } from 'jotai/utils';
import React, { useCallback, useRef, useState } from 'react';
import { compose, space, SpaceProps } from 'styled-system';

import { Alert, Box, Button, Drawer, Flex, Grid, Icon, Overlay } from '~/components/blocks';
import { GuestPatientForm } from '~/components/partials/GuestPatientForm';
import { Fields } from '~/components/partials/GuestPatientForm/types';
import { CheckinEntryItemFragment } from '~/graphql/generated/graphql';
import { newPatientFromCheckinEntryDrawerState } from '~/state/patient/atoms';

import { useCreateDirectVisitor } from '../../NewPatientDrawer/use-create-direct-visitor';
import { NewPatientInputForm } from '../NewPatientInputForm';
import { useDefaultValues } from './use-default_values';

type Props = {
  checkinEntry: CheckinEntryItemFragment;
  onBackClick?: () => void;
};

const InputFormRoot = styled('div', {
  shouldForwardProp,
})<SpaceProps>(
  ({ theme }) =>
    css({
      backgroundColor: theme.colors.background.bg,
      border: theme.borders.default,
      borderRadius: theme.radii.default,
      marginTop: theme.space.l,
      padding: `calc(${theme.space.s} + ${theme.space.m}) + ${theme.space.l}`,
      input: {
        backgroundColor: theme.colors.background.default,
        border: theme.borders.default,
      },
    }),
  compose(space),
);

export const NewPatientPanel = (props: Props) => {
  const theme = useTheme();
  const formRef = useRef<RefAttributeType<typeof GuestPatientForm>>(null);
  const { checkinEntry, onBackClick } = props;
  const [error, setError] = useState('');
  const resetState = useResetAtom(newPatientFromCheckinEntryDrawerState);

  const initialValues = useDefaultValues(checkinEntry);
  const { create, creating } = useCreateDirectVisitor();

  const isFinished = checkinEntry.status === 'finished';

  const handleSubmit = useCallback(
    async (values: Fields) => {
      try {
        await create(values);
        resetState();
      } catch (e) {
        setError(e);
      }
    },
    [create, resetState],
  );

  const handleSendClick = useCallback(() => {
    formRef.current?.submitForm();
  }, [formRef]);

  return (
    <>
      <Grid
        height="100%"
        gridTemplateRows={
          error
            ? 'min-content min-content min-content min-content'
            : 'min-content min-content min-content'
        }
        gridAutoColumns="1fr"
        padding={theme.space.l}
        overflow="auto"
      >
        {error && (
          <Alert status="error" marginBottom={theme.space.l}>
            {error}
          </Alert>
        )}
        {!isFinished && (
          <Alert status="error" marginBottom={theme.space.l}>
            チェックイン患者の服薬指導が完了していないため、患者登録できません。
            <br />
            「受付」ページにて該当患者の対応を完了してください。
          </Alert>
        )}
        <Box position="relative" height="100%">
          <Overlay open={!isFinished} inside />
          {isFinished && (
            <Alert status="info">
              この患者情報はチェックイン時に取得しました。
              <br />
              フォローアップを実施する場合は、患者情報を補完してください。
            </Alert>
          )}
          <InputFormRoot>
            <NewPatientInputForm
              ref={formRef}
              onSubmit={handleSubmit}
              initialValues={initialValues}
            />
          </InputFormRoot>
          {onBackClick && (
            <Flex justifyContent="flex-start" marginTop={theme.space.l}>
              <Button size="s" use="white" onClick={onBackClick}>
                <Icon icon="back-line" size="s" />
                戻る
              </Button>
            </Flex>
          )}
        </Box>
      </Grid>
      <Drawer.Footer>
        <Button
          use="base"
          wide="fill"
          size="l"
          onClick={handleSendClick}
          loading={creating}
          disabled={!isFinished}
        >
          登録
        </Button>
      </Drawer.Footer>
    </>
  );
};
